import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { runOrchestration, mockOrchestratorInput, ORCHESTRATOR_STATUS } from '../data/orchestratorLogic';

export const InterventionOrchestrator = () => {
  const [step, setStep] = useState('idle'); // idle, processing, result
  const [orchestration, setOrchestration] = useState(null);
  const [progress, setProgress] = useState(0);

  const startOrchestration = () => {
    setStep('processing');
    setOrchestration(null);
    setProgress(0);

    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          setOrchestration(runOrchestration(mockOrchestratorInput));
          setStep('result');
          return 100;
        }
        return prev + 2;
      });
    }, 50);
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">AI Intervention Orchestrator</h1>
          <p className="text-slate-500 mt-1 uppercase text-[10px] font-bold tracking-widest italic">Central Control & Automation Engine</p>
        </div>
        {step === 'result' && (
          <Button variant="primary" icon={<SafeIcon name="Share2" />}>Aktiver full utrulling</Button>
        )}
      </div>

      <AnimatePresence mode="wait">
        {step === 'idle' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-[60vh] flex items-center justify-center">
            <Card className="bg-slate-900 border-none text-white max-w-2xl w-full overflow-hidden relative shadow-3xl">
              <div className="absolute top-0 right-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-[100px] -mr-32 -mt-32" />
              <CardContent className="p-20 text-center space-y-10 relative z-10">
                <div className="w-20 h-20 bg-white/10 rounded-3xl flex items-center justify-center mx-auto backdrop-blur-md border border-white/10">
                  <SafeIcon name="Cpu" className="text-4xl text-indigo-400" />
                </div>
                <div className="space-y-4">
                  <h2 className="text-4xl font-black uppercase tracking-tighter">Klar for Orchestration</h2>
                  <p className="text-indigo-100/60 text-lg">
                    Siste kartleggings-syklus er fullført. Trykk for å generere alle strategiske leveranser automatisk.
                  </p>
                </div>
                <Button size="lg" onClick={startOrchestration} className="px-16 py-8 text-xl font-black uppercase tracking-widest bg-indigo-600 hover:bg-indigo-500 shadow-2xl">
                  Start AI workflow
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {step === 'processing' && (
          <motion.div key="proc" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-[60vh] flex items-center justify-center">
            <div className="max-w-md w-full space-y-8 text-center">
              <div className="relative w-32 h-32 mx-auto">
                 <div className="absolute inset-0 border-4 border-slate-100 rounded-full" />
                 <motion.div 
                    className="absolute inset-0 border-4 border-indigo-600 rounded-full border-t-transparent"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                 />
                 <div className="absolute inset-0 flex items-center justify-center">
                   <span className="text-2xl font-black text-slate-900">{progress}%</span>
                 </div>
              </div>
              <div className="space-y-2">
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-widest">Orchestrating Deliverables</h3>
                <p className="text-slate-400 text-sm font-medium animate-pulse italic">
                  {progress < 30 ? 'Beregner indekser...' : progress < 60 ? 'Maper risikomønstre...' : 'Genererer styrerapport og handlingsplaner...'}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {step === 'result' && orchestration && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Status & Alerts Column */}
            <div className="space-y-6">
              <Card className={`border-none shadow-xl ${orchestration.automationFlags.isPriorityCase ? 'bg-rose-900 text-white' : 'bg-slate-900 text-white'}`}>
                <CardHeader title="Orchestration Status" subtitle={`ID: ${orchestration.id}`} />
                <CardContent className="space-y-6">
                   <div className="flex items-center gap-4">
                     <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${orchestration.automationFlags.isPriorityCase ? 'bg-rose-500' : 'bg-emerald-500'}`}>
                        <SafeIcon name={orchestration.automationFlags.isPriorityCase ? 'Zap' : 'Check'} className="text-2xl" />
                     </div>
                     <div>
                       <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Gjeldende Modus</p>
                       <h4 className="text-xl font-black uppercase tracking-tight">{orchestration.status}</h4>
                     </div>
                   </div>
                   <div className="pt-4 border-t border-white/5 space-y-4">
                     {orchestration.alerts.map((alert, i) => (
                       <div key={i} className="flex gap-3 text-xs bg-white/5 p-4 rounded-2xl border border-white/5 items-start">
                         <SafeIcon name="AlertTriangle" className="text-rose-400 shrink-0 mt-0.5" />
                         <span className="font-medium">{alert}</span>
                       </div>
                     ))}
                   </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader title="Automatiseringsregler" icon={<SafeIcon name="Settings" />} />
                <CardContent className="space-y-4">
                  {[
                    { label: 'Priority Case Flag', active: orchestration.automationFlags.isPriorityCase },
                    { label: 'Escalation Trigger', active: orchestration.automationFlags.hasCriticalDept },
                    { label: 'Executive Alert', active: orchestration.automationFlags.isScoreDrop }
                  ].map((rule, i) => (
                    <div key={i} className="flex justify-between items-center py-2">
                       <span className="text-xs font-bold text-slate-600">{rule.label}</span>
                       <div className={`w-10 h-5 rounded-full p-1 transition-all ${rule.active ? 'bg-indigo-600' : 'bg-slate-200'}`}>
                         <div className={`w-3 h-3 bg-white rounded-full transition-all ${rule.active ? 'ml-5' : 'ml-0'}`} />
                       </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Deliverables Grid Column */}
            <div className="lg:col-span-2 space-y-6">
              <h3 className="text-sm font-black text-slate-400 uppercase tracking-widest ml-1">Genererte Leveranser (Klar for utsendelse)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {orchestration.deliverables.map((item) => (
                  <Card key={item.id} hover={true} className="group overflow-hidden">
                    <CardContent className="p-6 flex gap-6 items-center">
                      <div className="w-14 h-14 rounded-2xl bg-slate-50 text-slate-400 flex items-center justify-center shrink-0 group-hover:bg-indigo-600 group-hover:text-white transition-all">
                        <SafeIcon name={item.icon} size={24} />
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                           <span className="text-[9px] font-black text-indigo-600 uppercase tracking-widest">{item.type}</span>
                           <span className="text-[9px] font-black text-emerald-500 uppercase">{item.status}</span>
                        </div>
                        <h4 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors">{item.title}</h4>
                      </div>
                      <Button variant="ghost" size="sm" icon={<SafeIcon name="ArrowRight" />} />
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Summary View */}
              <Card className="bg-emerald-50 border-emerald-100">
                 <CardHeader title="Planlagt Oppfølging" icon={<SafeIcon name="Clock" className="text-emerald-600" />} />
                 <CardContent className="p-0 border-t border-emerald-100">
                    <div className="divide-y divide-emerald-100">
                       <div className="p-6 flex justify-between items-center">
                         <div className="space-y-1">
                           <h5 className="font-black text-emerald-900 text-sm uppercase tracking-tight">Neste pulsmåling</h5>
                           <p className="text-xs text-emerald-700/60 font-medium">Uke 34 - Fokus: Stress & Trygghet</p>
                         </div>
                         <Button variant="secondary" size="sm" className="bg-white border-none text-emerald-600">Endre dato</Button>
                       </div>
                       <div className="p-6 flex justify-between items-center">
                         <div className="space-y-1">
                           <h5 className="font-black text-emerald-900 text-sm uppercase tracking-tight">Statusmøte Styret</h5>
                           <p className="text-xs text-emerald-700/60 font-medium">12. September - Presentasjon av 90-dagers plan</p>
                         </div>
                         <Button variant="secondary" size="sm" className="bg-white border-none text-emerald-600">Book Møte</Button>
                       </div>
                    </div>
                 </CardContent>
              </Card>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};